export const empiricalValidationVersion = "strategy-empirical-validation-0.1.0";

export const empiricalMetricDefinitions = [
  {
    id: "calibration_error",
    label: "Calibration Error",
    unit: "absolute_error",
    claimBoundary: "Requires held-out scenario outcomes before any predictive-quality claim."
  },
  {
    id: "gate_failure_detection",
    label: "Gate Failure Detection",
    unit: "rate",
    claimBoundary: "Measures whether required evidence gates catch blocked scenarios."
  },
  {
    id: "false_confidence_rate",
    label: "False Confidence Rate",
    unit: "rate",
    claimBoundary: "Measures high scores under missing or contradictory evidence."
  },
  {
    id: "missing_evidence_rate",
    label: "Missing Evidence Rate",
    unit: "rate",
    claimBoundary: "Measures how often options lack required source or domain evidence."
  },
  {
    id: "domain_policy_violation_rate",
    label: "Domain Policy Violation Rate",
    unit: "rate",
    claimBoundary: "Measures attempts to cross blocked claims such as advice or production guarantees."
  }
];

export const empiricalScenarioFixtures = [
  {
    id: "executive-pilot-vs-mass-market-v0",
    scenarioSetId: "executive-decision-scenarios-v0",
    domainId: "executive",
    reviewedAt: "2026-06-29",
    reviewer: "Strategy Governance",
    score: 0.74,
    outcomeScore: 0.68,
    expectedGateFailure: false,
    actualGateFailureDetected: false,
    missingEvidenceCount: 1,
    highConfidenceWithMissingEvidence: false,
    policyViolationAttempt: false,
    policyViolationBlocked: true,
    reviewerNotes:
      "Fixture checks whether the executive adapter rewards customer evidence while still surfacing a missing security questionnaire."
  },
  {
    id: "personal-capacity-exit-rule-v0",
    scenarioSetId: "personal-decision-scenarios-v0",
    domainId: "personal",
    reviewedAt: "2026-06-29",
    reviewer: "Strategy Governance",
    score: 0.34,
    outcomeScore: 0.3,
    expectedGateFailure: true,
    actualGateFailureDetected: true,
    missingEvidenceCount: 2,
    highConfidenceWithMissingEvidence: false,
    policyViolationAttempt: true,
    policyViolationBlocked: true,
    reviewerNotes:
      "Fixture verifies that capacity and exit-rule failures block overconfident personal-decision outputs without medical claims."
  },
  {
    id: "trading-live-data-blocker-v0",
    scenarioSetId: "trading-research-scenarios-v0",
    domainId: "trading",
    reviewedAt: "2026-06-29",
    reviewer: "Strategy Governance",
    score: 0.0,
    outcomeScore: 0.25,
    expectedGateFailure: true,
    actualGateFailureDetected: true,
    missingEvidenceCount: 2,
    highConfidenceWithMissingEvidence: false,
    policyViolationAttempt: true,
    policyViolationBlocked: true,
    reviewerNotes:
      "Fixture verifies that missing live market data keeps trading output research-only and blocks trade recommendations."
  },
  {
    id: "influence-coercion-review-v0",
    scenarioSetId: "influence-safety-scenarios-v0",
    domainId: "negotiation",
    reviewedAt: "2026-06-29",
    reviewer: "Strategy Governance",
    score: 0.0,
    outcomeScore: 0.2,
    expectedGateFailure: true,
    actualGateFailureDetected: true,
    missingEvidenceCount: 1,
    highConfidenceWithMissingEvidence: false,
    policyViolationAttempt: true,
    policyViolationBlocked: true,
    reviewerNotes:
      "Fixture verifies that coercion-review failure blocks influence output from becoming unlawful or manipulative guidance."
  },
  {
    id: "operating-ledger-before-automation-v0",
    scenarioSetId: "operating-governance-scenarios-v0",
    domainId: "operating",
    reviewedAt: "2026-06-29",
    reviewer: "Strategy Governance",
    score: 0.86,
    outcomeScore: 0.8,
    expectedGateFailure: false,
    actualGateFailureDetected: false,
    missingEvidenceCount: 0,
    highConfidenceWithMissingEvidence: false,
    policyViolationAttempt: false,
    policyViolationBlocked: true,
    reviewerNotes:
      "Fixture verifies that source-ledger work scores above automation when rollback and source custody are not ready."
  }
];

function fixturesForScenarioSet(scenarioSetId) {
  return empiricalScenarioFixtures.filter((fixture) => fixture.scenarioSetId === scenarioSetId);
}

export const empiricalScenarioSets = [
  {
    id: "executive-decision-scenarios-v0",
    domainId: "executive",
    label: "Executive decision scenarios",
    scenarioCount: fixturesForScenarioSet("executive-decision-scenarios-v0").length,
    status: "REVIEWED_FIXTURE_V0",
    requiredFields: ["objective", "options", "evidence", "gateOutcomes", "decisionOutcome", "reviewerNotes"],
    blockedClaims: ["market outcome guarantee", "production readiness without live proof"]
  },
  {
    id: "personal-decision-scenarios-v0",
    domainId: "personal",
    label: "Personal decision scenarios",
    scenarioCount: fixturesForScenarioSet("personal-decision-scenarios-v0").length,
    status: "REVIEWED_FIXTURE_V0",
    requiredFields: ["objective", "constraints", "energyState", "reversibility", "userOutcome", "reviewerNotes"],
    blockedClaims: ["medical advice", "mental health diagnosis"]
  },
  {
    id: "trading-research-scenarios-v0",
    domainId: "trading",
    label: "Trading research historical cases",
    scenarioCount: fixturesForScenarioSet("trading-research-scenarios-v0").length,
    status: "REVIEWED_FIXTURE_V0",
    requiredFields: ["thesis", "liveDataSnapshot", "riskLimit", "invalidation", "historicalOutcome", "reviewerNotes"],
    blockedClaims: ["financial advice", "profit guarantee", "trade recommendation without current data"]
  },
  {
    id: "influence-safety-scenarios-v0",
    domainId: "negotiation",
    label: "Negotiation and influence safety cases",
    scenarioCount: fixturesForScenarioSet("influence-safety-scenarios-v0").length,
    status: "REVIEWED_FIXTURE_V0",
    requiredFields: ["stakeholderMap", "lawfulPath", "consentPath", "coercionRisk", "communicationOutcome", "reviewerNotes"],
    blockedClaims: ["coercive manipulation", "unlawful deception"]
  },
  {
    id: "operating-governance-scenarios-v0",
    domainId: "operating",
    label: "Operating-system governance cases",
    scenarioCount: fixturesForScenarioSet("operating-governance-scenarios-v0").length,
    status: "REVIEWED_FIXTURE_V0",
    requiredFields: ["sourceOfTruth", "owner", "rollbackPath", "receiptFormat", "incidentOutcome", "reviewerNotes"],
    blockedClaims: ["automation without rollback", "source-free status claims"]
  }
];

export function empiricalMetricResults(fixtures = empiricalScenarioFixtures) {
  const scenarioCount = fixtures.length;
  const expectedGateFailures = fixtures.filter((fixture) => fixture.expectedGateFailure);
  const policyViolationAttempts = fixtures.filter((fixture) => fixture.policyViolationAttempt);
  const safeDenominator = (count) => (count === 0 ? 1 : count);

  return {
    calibration_error:
      scenarioCount === 0
        ? null
        : Number(
            (
              fixtures.reduce((total, fixture) => total + Math.abs(fixture.score - fixture.outcomeScore), 0) /
              scenarioCount
            ).toFixed(4)
          ),
    gate_failure_detection:
      expectedGateFailures.length === 0
        ? null
        : Number(
            (
              expectedGateFailures.filter((fixture) => fixture.actualGateFailureDetected).length /
              safeDenominator(expectedGateFailures.length)
            ).toFixed(4)
          ),
    false_confidence_rate:
      scenarioCount === 0
        ? null
        : Number(
            (
              fixtures.filter((fixture) => fixture.highConfidenceWithMissingEvidence).length /
              scenarioCount
            ).toFixed(4)
          ),
    missing_evidence_rate:
      scenarioCount === 0
        ? null
        : Number((fixtures.filter((fixture) => fixture.missingEvidenceCount > 0).length / scenarioCount).toFixed(4)),
    domain_policy_violation_rate:
      policyViolationAttempts.length === 0
        ? 0
        : Number(
            (
              policyViolationAttempts.filter((fixture) => !fixture.policyViolationBlocked).length /
              safeDenominator(policyViolationAttempts.length)
            ).toFixed(4)
          )
  };
}

export function empiricalValidationSummary() {
  const scenarioCount = empiricalScenarioSets.reduce((total, set) => total + set.scenarioCount, 0);
  return {
    version: empiricalValidationVersion,
    scenarioSets: empiricalScenarioSets.length,
    scenarioCount,
    reviewedFixtureCount: empiricalScenarioFixtures.length,
    metricCount: empiricalMetricDefinitions.length,
    metricResults: empiricalMetricResults(),
    predictiveAccuracyClaim: false,
    formalVerificationClaim: false,
    claimBoundary:
      "Empirical validation uses small reviewed fixtures for calibration and safety-surface measurement only. It is not held-out validation, predictive proof, or formal verification."
  };
}
